import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { getSubscriptionDiscounts } from '../+state/subscription-discounts.actions';
import { getIsLoading, getIsLoaded, getShippingDiscounts } from '../+state/subscription-discounts.selectors';
import {
  SubscriptionDiscount,
  SubscriptionDiscounts,
} from '@api-ui-app/src/app/shared/subscription-discounts/types/subscription-discounts';
import { GelatoSubscriptionType } from '@gelato-api-ui/sdk/src/lib/shared/subscriptions-shared/constants/gelato-subscription.constant';
import { CurrencyRateService } from '@gelato-api-ui/core/currency-rate/services/currency-rate.service';

@Injectable({ providedIn: 'root' })
export class SubscriptionDiscountsFacade {
  shippingDiscounts$ = combineLatest([this.store.select(getIsLoaded), this.store.select(getIsLoading)]).pipe(
    tap(([isLoaded, isLoading]) => {
      if (!isLoaded && !isLoading) {
        this.loadSubscriptionDiscounts();
      }
    }),
    switchMap(() => this.store.select(getShippingDiscounts)),
  );

  constructor(private readonly store: Store, private readonly currencyRateService: CurrencyRateService) {}

  getConvertedShippingDiscounts(currency: string): Observable<SubscriptionDiscounts['shipping']> {
    return this.shippingDiscounts$.pipe(
      filter(shippingDiscounts => !!shippingDiscounts),
      switchMap(shippingDiscounts => {
        return forkJoin(
          Object.entries(shippingDiscounts).reduce(
            (acc, [subscriptionType, discount]) => ({
              ...acc,
              [subscriptionType]: this.convertShippingDiscount(discount, currency),
            }),
            {},
          ),
        );
      }),
    );
  }

  getConvertedShippingDiscountBySubscriptionType(
    subscriptionType: GelatoSubscriptionType,
    currency: string,
  ): Observable<SubscriptionDiscount> {
    return this.shippingDiscounts$.pipe(
      map(shippingDiscounts => shippingDiscounts?.[subscriptionType] ?? null),
      switchMap(shippingDiscount => this.convertShippingDiscount(shippingDiscount, currency)),
    );
  }

  private convertShippingDiscount(
    discount: SubscriptionDiscount,
    selectedCurrency: string,
  ): Observable<SubscriptionDiscount> {
    if (!discount) {
      return of(null);
    }

    return this.currencyRateService.get(discount.threshold.currency, selectedCurrency).pipe(
      map(rate => ({
        ...discount,
        threshold: {
          ...discount.threshold,
          amount: discount.threshold.amount * rate,
        },
      })),
    );
  }

  private loadSubscriptionDiscounts(): void {
    this.store.dispatch(getSubscriptionDiscounts());
  }
}
